import {
  BadgeCheck,
  CircleCheck,
  CircleDashed,
  CircleSlash,
  CircleX,
  Clock3,
  FlaskConical,
  Hourglass,
  LoaderCircle,
  PencilLine,
  PenLine,
  ClipboardCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { DocumentStatus, EtaEnvironment, EtaStatus, Readiness } from '../../api/types';
import { useI18n } from '../../i18n/I18nProvider';
import { Badge, type Tone } from '../../ui/Feedback';
import styles from './Sales.module.css';

/** The tax-status axis shown to users (D-1 v2): readiness for drafts, the e-seal step, then ETA. */
export type TaxAxis = Readiness | EtaStatus | 'signing' | 'signed';

const TAX_TONE: Record<TaxAxis, Tone> = {
  incomplete: 'warning',
  ready: 'info',
  signing: 'warning',
  signed: 'info',
  not_submitted: 'neutral',
  submitting: 'warning',
  submitted: 'warning',
  valid: 'success',
  invalid: 'danger',
  rejected: 'danger',
  not_accepted: 'danger',
  cancelled: 'neutral',
};

const TAX_ICON: Record<TaxAxis, ReactNode> = {
  incomplete: <CircleDashed size={13} aria-hidden="true" />,
  ready: <ClipboardCheck size={13} aria-hidden="true" />,
  signing: <PenLine size={13} aria-hidden="true" />,
  signed: <BadgeCheck size={13} aria-hidden="true" />,
  not_submitted: <Clock3 size={13} aria-hidden="true" />,
  submitting: <LoaderCircle size={13} aria-hidden="true" className={styles.spin} />,
  submitted: <Hourglass size={13} aria-hidden="true" />,
  valid: <CircleCheck size={13} aria-hidden="true" />,
  invalid: <CircleX size={13} aria-hidden="true" />,
  rejected: <CircleX size={13} aria-hidden="true" />,
  not_accepted: <CircleSlash size={13} aria-hidden="true" />,
  cancelled: <CircleSlash size={13} aria-hidden="true" />,
};

/** States in which the screen keeps refreshing on its own (6.05: no manual reload). */
export const LIVE_STATES: ReadonlySet<string> = new Set(['submitting', 'submitted']);

export function taxAxisOf(row: {
  status: DocumentStatus;
  readiness?: Readiness | null;
  etaStatus?: EtaStatus | null;
}): TaxAxis | null {
  if (row.status === 'discarded') return null;
  if (row.status === 'draft') return row.readiness ?? 'incomplete';
  // `not_submitted` is the read model's "no submission yet" (the detail page always returns it),
  // so it is the same as no ETA state at all: the signing step (WP-1b.1) shows until then.
  const eta = row.etaStatus && row.etaStatus !== 'not_submitted' ? row.etaStatus : null;
  if (!eta && (row.status === 'signing' || row.status === 'signed')) return row.status;
  return eta ?? 'not_submitted';
}

export function TaxStatusBadge({ value, testId }: { value: TaxAxis; testId?: string }) {
  const { t } = useI18n();
  return (
    <span data-testid={testId} data-status={value} className={styles.badgeWrap}>
      <Badge tone={TAX_TONE[value]} icon={TAX_ICON[value]}>
        {t.taxStatus[value]}
      </Badge>
    </span>
  );
}

const DOC_TONE: Record<DocumentStatus, Tone> = {
  draft: 'neutral',
  issued: 'primary',
  signing: 'warning',
  signed: 'primary',
  superseded: 'neutral',
  discarded: 'neutral',
};

export function DocStatusBadge({ value, testId }: { value: DocumentStatus; testId?: string }) {
  const { t } = useI18n();
  return (
    <span data-testid={testId} data-status={value} className={styles.badgeWrap}>
      <Badge
        tone={DOC_TONE[value]}
        icon={value === 'draft' ? <PencilLine size={13} aria-hidden="true" /> : undefined}
        className={value === 'discarded' ? styles.voidBadge : undefined}
      >
        {t.docStatus[value]}
      </Badge>
    </span>
  );
}

/** Every ETA status that comes from the mock adapter carries this label (plan §2). */
export function MockLabel({ environment }: { environment: EtaEnvironment | null | undefined }) {
  const { t } = useI18n();
  if (environment === 'production' || !environment) return null;
  return (
    <span className={styles.mock} title={t.mock.title} data-testid="mock-label">
      <FlaskConical size={12} aria-hidden="true" />
      {environment === 'mock' ? t.mock.label : 'PREPROD'}
    </span>
  );
}
